// src/pages/PrivacyPage.js
// Politique de confidentialité CoryntiQ — modèle adapté à la Loi 25
// ⚠️ À FAIRE RELIRE PAR UN CONSEILLER JURIDIQUE AVANT LE LANCEMENT.
//
// À PERSONNALISER (cherche "À CONFIRMER" ci-dessous) :
//   1. Raison sociale
//   2. Nom du responsable de la protection des renseignements personnels
//   3. Courriel de contact vie privée
//   4. Date d'entrée en vigueur

import React from "react";
import { Shield, ArrowLeft } from "lucide-react";

const DERNIERE_MAJ = "17 juin 2026";

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl font-bold text-[#1E3A5F] mb-3">{title}</h2>
    <div className="space-y-3 text-slate-700 leading-relaxed">{children}</div>
  </section>
);

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50 font-[Inter]">
      {/* En-tête */}
      <header className="bg-[#1E3A5F] text-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-white/15 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#0EA5E9]" />
            </div>
            <span className="text-lg font-bold">CoryntiQ</span>
          </a>
          <a
            href="/"
            className="flex items-center gap-1 text-sm text-sky-200 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </a>
        </div>
      </header>

      {/* Contenu */}
      <main className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="text-3xl font-bold text-[#1E3A5F] mb-2">
          Politique de confidentialité
        </h1>
        <p className="text-slate-500 mb-8">
          Dernière mise à jour : {DERNIERE_MAJ}
        </p>

        <Section title="1. Qui sommes-nous">
          <p>
            CoryntiQ est un logiciel exploité par <strong>CIGP Inc.</strong> («
            CoryntiQ », « nous »), une entreprise établie au Québec, Canada.
          </p>
          <p>
            La présente politique explique quels renseignements personnels nous
            recueillons, pourquoi, comment nous les protégeons, et quels sont
            vos droits en vertu de la{" "}
            <em>
              Loi sur la protection des renseignements personnels dans le
              secteur privé
            </em>{" "}
            (Loi 25).
          </p>
        </Section>

        <Section title="2. Responsable de la protection des renseignements personnels">
          <p>
            Conformément à la Loi 25, nous avons désigné un responsable de la
            protection des renseignements personnels :
          </p>
          <p className="bg-white border border-slate-200 rounded-lg p-4">
            <strong>Micknel Toussaint</strong> {/* À CONFIRMER */}
            <br />
            Courriel : confidentialite@coryntiq.ca {/* À CONFIRMER */}
          </p>
          <p>
            Vous pouvez le joindre pour toute question relative à vos
            renseignements personnels ou pour exercer vos droits.
          </p>
        </Section>

        <Section title="3. Renseignements que nous recueillons">
          <p>Nous recueillons uniquement les renseignements nécessaires :</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>Compte</strong> : prénom, nom, courriel, mot de passe
              (chiffré).
            </li>
            <li>
              <strong>Organisation</strong> : nom de l'entreprise, secteur,
              nombre d'employés, téléphone.
            </li>
            <li>
              <strong>Facturation</strong> : plan choisi et jeton de paiement
              sécurisé. Nous ne stockons jamais votre numéro de carte complet —
              il est traité par notre fournisseur Moneris.
            </li>
            <li>
              <strong>Données de conformité</strong> : les renseignements que
              vous saisissez dans l'application (inventaires, incidents,
              consentements, etc.). Vous en demeurez responsable.
            </li>
            <li>
              <strong>Usage technique</strong> : journaux de connexion et
              données nécessaires au bon fonctionnement et à la sécurité du
              service.
            </li>
          </ul>
        </Section>

        <Section title="4. Finalités">
          <p>Nous utilisons ces renseignements pour :</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>fournir et maintenir le service CoryntiQ;</li>
            <li>gérer votre compte, votre abonnement et votre facturation;</li>
            <li>assurer la sécurité et prévenir la fraude;</li>
            <li>vous offrir du support;</li>
            <li>vous informer de changements importants au service.</li>
          </ul>
        </Section>

        <Section title="5. Hébergement et localisation des données">
          <p>
            Vos données sont hébergées au <strong>Canada</strong> (région
            ca-central) chez notre fournisseur d'infrastructure Supabase. Les
            paiements sont traités au Canada par Moneris. Nous privilégions des
            fournisseurs canadiens afin que vos renseignements demeurent au
            pays.
          </p>
        </Section>

        <Section title="6. Partage avec des tiers">
          <p>
            Nous ne vendons jamais vos renseignements personnels. Nous faisons
            appel à des sous-traitants strictement nécessaires au service :
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>Supabase</strong> — hébergement et base de données
              (Canada).
            </li>
            <li>
              <strong>Moneris</strong> — traitement des paiements (Canada).
            </li>
          </ul>
          <p>
            Ces fournisseurs sont tenus contractuellement de protéger vos
            renseignements et de ne les utiliser que pour les fins prévues.
          </p>
        </Section>

        <Section title="7. Durée de conservation">
          <p>
            Nous conservons vos renseignements aussi longtemps que votre compte
            est actif, puis pour la période nécessaire au respect de nos
            obligations légales et fiscales. À la fermeture de votre compte,
            vous pouvez demander la suppression de vos données, sous réserve des
            obligations de conservation applicables.
          </p>
        </Section>

        <Section title="8. Sécurité">
          <p>
            Nous mettons en œuvre des mesures de sécurité raisonnables :
            chiffrement en transit (HTTPS), mots de passe chiffrés, contrôle des
            accès et hébergement sécurisé au Canada. Aucun système n'étant
            infaillible, nous ne pouvons garantir une sécurité absolue, mais
            nous traitons toute incident de confidentialité avec sérieux et
            diligence.
          </p>
        </Section>

        <Section title="9. Vos droits">
          <p>En vertu de la Loi 25, vous avez le droit de :</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>accéder à vos renseignements personnels;</li>
            <li>les faire rectifier s'ils sont inexacts ou incomplets;</li>
            <li>retirer votre consentement;</li>
            <li>demander la portabilité de vos renseignements;</li>
            <li>
              demander la cessation de la diffusion ou la désindexation de
              certains renseignements;
            </li>
            <li>
              être informé lorsqu'une décision est fondée exclusivement sur un
              traitement automatisé.
            </li>
          </ul>
          <p>
            Pour exercer ces droits, écrivez à notre responsable (section 2).
            Nous répondons dans les délais prévus par la loi.
          </p>
        </Section>

        <Section title="10. Témoins (cookies)">
          <p>
            Nous utilisons des témoins essentiels au fonctionnement du service
            (authentification, session). Nous n'utilisons pas de témoins
            publicitaires.
          </p>
        </Section>

        <Section title="11. Incidents de confidentialité">
          <p>
            En cas d'incident de confidentialité présentant un risque de
            préjudice sérieux, nous prenons les mesures requises et procédons
            aux notifications prévues par la Loi 25, notamment auprès de la
            Commission d'accès à l'information (CAI) et des personnes concernées
            lorsque la loi l'exige.
          </p>
        </Section>

        <Section title="12. Plaintes">
          <p>
            Si vous estimez que vos droits n'ont pas été respectés, vous pouvez
            communiquer avec notre responsable, puis, le cas échéant, porter
            plainte auprès de la Commission d'accès à l'information du Québec
            (CAI).
          </p>
        </Section>

        <Section title="13. Modifications">
          <p>
            Nous pouvons modifier la présente politique. Toute modification
            importante vous sera communiquée et la date de mise à jour sera
            révisée en haut de cette page.
          </p>
        </Section>

        <div className="mt-12 pt-6 border-t border-slate-200 text-sm text-slate-500">
          <a href="/terms" className="text-[#0EA5E9] hover:underline">
            Conditions d'utilisation
          </a>{" "}
          · © {new Date().getFullYear()} CIGP Inc. — CoryntiQ
        </div>
      </main>
    </div>
  );
};

export default PrivacyPage;
